import type { ModelNode, ModelEdge } from '../types/conceptMap';
import type { CanvasSize, PositionedNode } from './types';

const COLUMN_GAP = 320;
const TRAIT_SPACING_Y = 50;
const HEADER_Y = 60;
const TRAITS_START_Y = 140;
const SHARED_MARGIN_Y = 60;

/**
 * Contrast (mirror) layout: two concepts side-by-side with traits below,
 * shared traits centered at bottom.
 * Pure function — same input always produces same output.
 */
export function contrastLayout(
  nodes: ModelNode[],
  edges: ModelEdge[],
  canvasSize: CanvasSize
): PositionedNode[] {
  if (nodes.length === 0) return [];

  const centerX = canvasSize.width / 2;

  // Build parent→children map from edges
  const childrenOf = new Map<string, string[]>();
  for (const edge of edges) {
    const children = childrenOf.get(edge.source) ?? [];
    children.push(edge.target);
    childrenOf.set(edge.source, children);
  }

  const leftConcept = nodes.find((n) => n.role === 'contrast-left');
  const rightConcept = nodes.find((n) => n.role === 'contrast-right');
  const sharedTraits = nodes.filter((n) => n.role === 'shared-trait');

  const leftChildIds = new Set(childrenOf.get(leftConcept?.id ?? '') ?? []);
  const rightChildIds = new Set(childrenOf.get(rightConcept?.id ?? '') ?? []);

  const leftTraits = nodes.filter((n) => leftChildIds.has(n.id));
  const rightTraits = nodes.filter((n) => rightChildIds.has(n.id));

  const positions = new Map<string, { x: number; y: number }>();

  const leftX = centerX - COLUMN_GAP / 2;
  const rightX = centerX + COLUMN_GAP / 2;

  // Main concepts at top
  if (leftConcept) positions.set(leftConcept.id, { x: leftX, y: HEADER_Y });
  if (rightConcept) positions.set(rightConcept.id, { x: rightX, y: HEADER_Y });

  // Left traits
  leftTraits.forEach((node, i) => {
    positions.set(node.id, { x: leftX, y: TRAITS_START_Y + i * TRAIT_SPACING_Y });
  });

  // Right traits
  rightTraits.forEach((node, i) => {
    positions.set(node.id, { x: rightX, y: TRAITS_START_Y + i * TRAIT_SPACING_Y });
  });

  // Shared traits centered at bottom
  const maxTraitsY =
    TRAITS_START_Y + Math.max(leftTraits.length, rightTraits.length) * TRAIT_SPACING_Y;
  sharedTraits.forEach((node, i) => {
    positions.set(node.id, {
      x: centerX,
      y: maxTraitsY + SHARED_MARGIN_Y + i * TRAIT_SPACING_Y,
    });
  });

  return nodes.map((node) => {
    const pos = positions.get(node.id) ?? { x: centerX, y: HEADER_Y };
    return { ...node, x: pos.x, y: pos.y };
  });
}
